import React, { useEffect, useState, useRef } from "react";
import { api, JERSEY_STYLES, fmt } from "@/api";
import { JerseyBadge, ScorePill, BigTimer } from "@/components/Shared";
import ScheduleItinerary from "@/components/ScheduleItinerary";
import RecentResults from "@/components/RecentResults";
import { Gift } from "lucide-react";

export default function Watch() {
  const [data, setData] = useState({ live: null, on_deck: null });
  const [loading, setLoading] = useState(true);
  const [wishlistUrl, setWishlistUrl] = useState("");
  const timer = useRef(null);

  const load = async () => {
    try {
      const d = await api.live();
      setData(d);
    } catch (e) {
      // keep last known
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.config().then((c) => setWishlistUrl((c.amazon_wishlist_url || "").trim())).catch(() => {});
    timer.current = setInterval(load, 3000);
    return () => clearInterval(timer.current);
  }, []);

  const WishlistCard = () =>
    wishlistUrl ? (
      <a
        href={wishlistUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="watch-wishlist-card"
        className="block brutal p-4 mt-6 bg-[#FFD700] active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center gap-3">
          <Gift size={28} className="shrink-0" />
          <div className="min-w-0">
            <div className="font-display font-black uppercase tracking-tight text-lg leading-none">Help Stock the Event</div>
            <p className="font-display text-xs text-black/70 mt-1">
              Buy gear off our Amazon wishlist — it ships straight to us. Mahalo! 🤙
            </p>
          </div>
          <span className="ml-auto font-display font-black uppercase text-xs tracking-widest underline shrink-0">Shop →</span>
        </div>
      </a>
    ) : null;

  const live = data.live;
  const onDeck = data.on_deck;

  if (loading) {
    return <div className="p-8 text-center font-display uppercase tracking-widest text-gray-400">Loading…</div>;
  }

  if (data.event_live === false) {
    return (
      <div className="max-w-lg mx-auto p-4 pt-16 text-center" data-testid="event-not-live">
        <div className="brutal p-8">
          <div className="font-score text-5xl sm:text-6xl leading-none">Contest Hasn't<br />Started Yet</div>
          <p className="font-display text-gray-600 mt-4 uppercase tracking-widest text-sm">
            Pounders Fest live scoring goes here once we drop in.
          </p>
          <p className="font-display text-gray-500 mt-2">
            Holding period: first &amp; second weekends of September 2026. Check the <strong>Draw</strong> tab to find your heat, or <strong>Enter</strong> to register.
          </p>
        </div>
        <ScheduleItinerary />
        <WishlistCard />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 pb-24" data-testid="watch-view">
      {!live ? (
        <div className="brutal p-8 text-center mt-6" data-testid="no-live-heat">
          <div className="font-score text-5xl">NO HEAT LIVE</div>
          <p className="font-display text-gray-500 mt-2 uppercase tracking-widest text-sm">
            Hang tight — the next heat is loading up
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-baseline justify-between mt-2 mb-3 flex-wrap gap-2">
            <div>
              <div className="font-display text-xs tracking-[0.25em] uppercase text-gray-500">
                {live.division_name}{live.round_name ? ` · ${live.round_name}` : ""}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight leading-none">
                {live.name}
              </h1>
            </div>
            <span className="brutal-2 bg-[#E63946] text-white font-black uppercase px-3 py-1 text-sm tracking-widest">
              ● Live
            </span>
          </div>

          <BigTimer timer={live.timer} />

          {live.hide_scores ? (
            <div className="mt-4" data-testid="final-hidden">
              <div className="brutal p-4 bg-[#FFD700] text-center mb-3">
                <div className="font-display font-black uppercase tracking-tight text-lg">🔒 Final In Progress</div>
                <p className="font-display text-sm text-black/70 mt-1">Scores are hidden — the champion is revealed at the closing ceremony!</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {live.riders.map((r) => (
                  <div key={r.rider_id} className="brutal flex items-center gap-2 p-3" data-testid={`final-rider-${r.jersey}`}>
                    <JerseyBadge jersey={r.jersey} />
                    <span className="font-display font-bold uppercase truncate text-sm">{r.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
          <div className="mt-4 space-y-3" data-testid="leaderboard">
            {live.leaderboard.map((r) => (
              <div
                key={r.rider_id}
                className="brutal flex items-center gap-3 p-3"
                data-testid={`leaderboard-row-${r.jersey}`}
              >
                <div className="font-score text-4xl w-8 text-center text-gray-400">{r.rank}</div>
                <JerseyBadge jersey={r.jersey} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold uppercase truncate text-lg leading-tight">{r.name}</div>
                  {r.advancing === true ? (
                    <span className="font-display text-[10px] font-black uppercase tracking-widest text-green-600" data-testid={`advancing-${r.jersey}`}>▲ Advancing</span>
                  ) : r.needs != null ? (
                    <span className="font-display text-[10px] font-black uppercase tracking-widest text-[#E63946]" data-testid={`needs-${r.jersey}`}>Needs {fmt(r.needs)} to advance</span>
                  ) : null}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {r.waves.length === 0 ? (
                      <span className="text-gray-400 text-xs uppercase tracking-widest">No waves yet</span>
                    ) : (
                      r.waves.map((w) => <ScorePill key={w.index} w={w} />)
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-score text-6xl sm:text-7xl leading-none">{fmt(r.total)}</div>
                  <div className="font-display text-[10px] tracking-[0.2em] uppercase text-gray-500">Best 2</div>
                </div>
              </div>
            ))}
          </div>
          )}
        </>
      )}

      {onDeck && (
        <div className="brutal mt-6 p-4 bg-gray-100" data-testid="on-deck-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-black uppercase tracking-tight">On Deck</h2>
            <span className="font-display text-xs uppercase tracking-widest text-gray-500">
              {onDeck.division_name} · {onDeck.name}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {onDeck.riders.map((r) => (
              <div key={r.rider_id} className="brutal-2 bg-white flex items-center gap-2 p-2">
                <JerseyBadge jersey={r.jersey} size="sm" />
                <span className="font-display font-bold uppercase text-sm truncate">{r.name}</span>
              </div>
            ))}
          </div>
          <p className="font-display text-xs uppercase tracking-widest text-gray-600 mt-3">
            Grab your jerseys &amp; head to the marshal →
          </p>
        </div>
      )}

      <RecentResults />
      <ScheduleItinerary />
      <WishlistCard />
    </div>
  );
}
